import './assets/index.css';
import { createApp, watch } from 'vue';
import { createPinia } from 'pinia';

import App from './App.vue';
import router from './router';
import { createAuth0 } from '@auth0/auth0-vue';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { createI18n } from 'vue-i18n';
import enUS from './locales/en-US.json';
import deDE from './locales/de-DE.json';
import * as Sentry from '@sentry/vue';

const organizationId = import.meta.env.VITE_ORGANIZATION_ID || 'manta';
const organizationName = import.meta.env.VITE_ORGANIZATION_NAME || 'Manta';

const app = createApp(App);
const pinia = createPinia();

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    enabled: import.meta.env.VITE_APP_ENV === 'production',
    environment: import.meta.env.VITE_APP_ENV || 'development',
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: 0.05,
    initialScope: {
      tags: { organizationId, organizationName },
      contexts: {
        organization: { id: organizationId, name: organizationName },
      },
    },
  });
}

const auth0 = createAuth0({
  domain: import.meta.env.VITE_AUTH0_DOMAIN,
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
  authorizationParams: {
    redirect_uri: import.meta.env.VITE_AUTH0_CALLBACK_URL,
    audience: import.meta.env.VITE_AUTH0_AUDIENCE,
  },
});

/**
 * Resolves once Auth0 has finished its initial session check, so callers read a
 * settled `isAuthenticated` instead of its initial `false`. Falls back to
 * resolving after `timeoutMs` so a wedged session check can never hang
 * navigation indefinitely.
 */
export function waitForAuth0Ready(timeoutMs = 5000): Promise<void> {
  if (!auth0.isLoading.value) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      stop();
      resolve();
    }, timeoutMs);
    const stop = watch(auth0.isLoading, (loading) => {
      if (!loading) {
        clearTimeout(timer);
        stop();
        resolve();
      }
    });
  });
}

type MessageSchema = typeof enUS;

const i18n = createI18n<[MessageSchema], 'en-US' | 'de-DE'>({
  legacy: false,
  locale: 'de-DE',
  fallbackLocale: 'en-US',
  messages: {
    'en-US': enUS,
    'de-DE': deDE,
  },
});

app.use(VueQueryPlugin);
app.use(pinia);
app.use(router);
app.use(i18n);
app.use(auth0);

app.mount('#app');

export { auth0, i18n };
